import apiClient from '@/api/client';
import { useAuthStore } from '@/store/authStore';
import { PHOTOS } from '@/api/endpoints';
import type { CursorPaginatedResponse } from '@/api/types/common.types';
import type {
  PhotoBulkActionPayload,
  PhotoBulkActionResponse,
  PhotoDetail,
  PhotoFilters,
  PhotoListItem,
  PhotoUpdatePayload,
} from '@/api/types/photo.types';

// ---------------------------------------------------------------------------
// List & Detail
// ---------------------------------------------------------------------------

/**
 * Fetch a cursor-paginated list of photos with optional filters.
 * When `cursor` is provided the API returns the next page.
 */
export async function getPhotos(
  params?: PhotoFilters,
): Promise<CursorPaginatedResponse<PhotoListItem>> {
  const { data } = await apiClient.get<CursorPaginatedResponse<PhotoListItem>>(
    PHOTOS.LIST,
    { params },
  );
  return data;
}

/** Fetch full detail for a single photo. */
export async function getPhoto(photoId: string): Promise<PhotoDetail> {
  const { data } = await apiClient.get<PhotoDetail>(PHOTOS.DETAIL(photoId));
  return data;
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/** Partially update a photo (star / hide / phase). */
export async function updatePhoto(
  photoId: string,
  payload: PhotoUpdatePayload,
): Promise<PhotoDetail> {
  const { data } = await apiClient.patch<PhotoDetail>(
    PHOTOS.UPDATE(photoId),
    payload,
  );
  return data;
}

/** Soft-delete a photo (sets is_hidden = true). */
export async function deletePhoto(photoId: string): Promise<void> {
  await apiClient.delete(PHOTOS.DELETE(photoId));
}

/** Execute a batch operation on multiple photos. */
export async function bulkAction(
  payload: PhotoBulkActionPayload,
): Promise<PhotoBulkActionResponse> {
  const { data } = await apiClient.post<PhotoBulkActionResponse>(
    PHOTOS.BULK_ACTION,
    payload,
  );
  return data;
}

// ---------------------------------------------------------------------------
// File / Thumbnail URLs
// ---------------------------------------------------------------------------

/**
 * Build the full URL to the original photo file.
 * This can be used as an `<img src>` or for downloads.
 */
export function getPhotoFileUrl(photoId: string): string {
  const token = useAuthStore.getState().accessToken || '';
  return `${apiClient.defaults.baseURL}${PHOTOS.SERVE_FILE(photoId)}?token=${token}`;
}

/**
 * Build the full URL to a photo thumbnail.
 * @param size - "small" | "medium" | "large"
 */
export function getPhotoThumbnailUrl(
  photoId: string,
  size: 'small' | 'medium' | 'large' = 'medium',
): string {
  const token = useAuthStore.getState().accessToken || '';
  return `${apiClient.defaults.baseURL}${PHOTOS.SERVE_THUMBNAIL(photoId)}?size=${size}&token=${token}`;
}
